/**
 * Stepper Component - Controller
 * ==============================
 */

import type { IScope, ISCEService, INgModelController, ITranscludeFunction } from 'angular';
import { SIZES, BASE_CLASSES, ICONS, DEFAULTS, StepperSize } from './constants';

export interface StepperBindings {
  label?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  size?: StepperSize;
  ariaLabel?: string;
  onChange?: (args: { value: number }) => void;
}

export class StepperController implements StepperBindings {
  static $inject = ['$scope', '$sce', '$transclude'];

  private $scope: IScope | null;
  private $sce: ISCEService | null;
  private $transclude: ITranscludeFunction | null;

  ngModelCtrl?: INgModelController | null;

  label?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  size?: StepperSize;
  ariaLabel?: string;
  onChange?: (args: { value: number }) => void;

  value = 0;
  trustedMinus: unknown = null;
  trustedPlus: unknown = null;

  // Slot nombrado (label): detectado con isSlotFilled, que SÍ funciona
  // para multi-slot transclusion (a diferencia del slot default booleano).
  hasLabelSlot = false;

  constructor($scope: IScope, $sce: ISCEService, $transclude: ITranscludeFunction) {
    this.$scope = $scope;
    this.$sce = $sce;
    this.$transclude = $transclude;
  }

  $onInit(): void {
    this.size = this.size || DEFAULTS.size;
    if (this.min == null) this.min = DEFAULTS.min;
    if (this.max == null) this.max = DEFAULTS.max;
    if (this.step == null) this.step = DEFAULTS.step;
    this.value = this.min;

    if (this.$sce) {
      this.trustedMinus = this.$sce.trustAsHtml(ICONS.minus);
      this.trustedPlus = this.$sce.trustAsHtml(ICONS.plus);
    }

    if (this.$transclude) {
      this.hasLabelSlot = this.$transclude.isSlotFilled('labelSlot');
    }

    if (this.ngModelCtrl) {
      this.ngModelCtrl.$render = () => {
        const v = this.ngModelCtrl ? Number(this.ngModelCtrl.$viewValue) : NaN;
        this.value = isNaN(v) ? (this.min as number) : v;
      };
    }
  }

  $onDestroy(): void {
    this.$scope = null;
    this.$sce = null;
    this.$transclude = null;
    this.ngModelCtrl = null;
  }

  private _commit(): void {
    if (this.ngModelCtrl) {
      this.ngModelCtrl.$setViewValue(this.value);
    }
    if (typeof this.onChange === 'function') {
      this.onChange({ value: this.value });
    }
  }

  increment(): void {
    if (this.plusDisabled) return;
    this.value = Math.min(this.max as number, this.value + (this.step as number));
    this._commit();
  }

  decrement(): void {
    if (this.minusDisabled) return;
    this.value = Math.max(this.min as number, this.value - (this.step as number));
    this._commit();
  }

  getWrapperClasses(): string[] {
    return [BASE_CLASSES.wrapper];
  }

  getControlClasses(): string[] {
    const classes = [BASE_CLASSES.control];
    if (this.disabled) classes.push(BASE_CLASSES.controlDisabled);
    return classes;
  }

  getLabelClasses(): string[] {
    const classes = [SIZES[this.size || DEFAULTS.size].label, BASE_CLASSES.label];
    if (this.disabled) classes.push(BASE_CLASSES.labelDisabled);
    return classes;
  }

  getValueClasses(): string[] {
    const classes = [SIZES[this.size || DEFAULTS.size].value, BASE_CLASSES.value];
    if (this.disabled) classes.push(BASE_CLASSES.valueDisabled);
    return classes;
  }

  getIconClasses(): string[] {
    return [SIZES[this.size || DEFAULTS.size].icon];
  }

  getMinusClasses(): string[] {
    const classes = [BASE_CLASSES.button, SIZES[this.size || DEFAULTS.size].button];
    if (this.minusDisabled) classes.push(BASE_CLASSES.buttonDisabled);
    return classes;
  }

  getPlusClasses(): string[] {
    const classes = [BASE_CLASSES.button, SIZES[this.size || DEFAULTS.size].button];
    if (this.plusDisabled) classes.push(BASE_CLASSES.buttonDisabled);
    return classes;
  }

  get minusDisabled(): boolean {
    return this.disabled === true || this.value <= (this.min as number);
  }

  get plusDisabled(): boolean {
    return this.disabled === true || this.value >= (this.max as number);
  }
}

export default StepperController;
